import { axiosCore } from './http-core-service';
import { MeetingDto, MyProxyGivenToDto } from './dtos/meeting.dto';
import { ResultSearchDto } from './dtos/result-search.dto';
import { MeetingFormDto } from './dtos/meeting-form.dto';
import { ProxyEligibilityDto } from './dtos/proxy-eligibility.dto';

export interface MeetingFileMetadataDto {
    documentId?: string;
    fileName?: string;
}

/**
 * Récupération des réunions.
 * @param mandateId identifiant du mandat de l'utilisateur
 * @param status statut de l'appel
 * @param page numéro de page
 * @param itemsPerPage nombre d'item par page
 * @param order ordre de tri
 * @param sort tri
 * @param form form
 */
export const getMeetings = async (
    mandateId: string,
    status: string,
    page: number,
    itemsPerPage: number,
    order?: string,
    sort?: string,
    form?: MeetingFormDto
): Promise<ResultSearchDto<MeetingDto>> => {
    let criterions = '';
    if (form?.date) {
        criterions += `&startDate=${form.date}&endDate=${form.date}`;
    }
    switch (form?.hasDocument) {
        case 't':
            criterions += '&hasDocument=true';
            break;
        case 'f':
            criterions += '&hasDocument=false';
            break;
        default:
            break;
    }
    if (form?.commission) {
        criterions += `&commission=${form?.commission}`;
    }
    if (!form?.date && form?.startDate) {
        criterions += `&startDate=${form?.startDate}`;
    }
    if (!form?.date && form?.endDate) {
        criterions += `&endDate=${form?.endDate}`;
    }
    if (form?.location) {
        criterions += `&location=${form?.location}`;
    }
    if (sort) {
        criterions += `&sort=${sort}`;
    }
    if (sort && order) {
        criterions += `&${order}=${sort}`;
    }
    // Pour le back, la page doit être incrémentée une fois
    page = page + 1;

    // Selon le parametre de l'état, on adapte la requete
    const response = await axiosCore.get(
        `/api/meetings/mandate/${mandateId}/list?status=${status}&page=${page}&itemsPerPage=${itemsPerPage}${criterions}`
    );

    return response.data;
};

export const getUpcomingMeetingsForCurrentUser = async (limit = 5): Promise<ResultSearchDto<MeetingDto>> => {
    const response = await axiosCore.get(`/api/meetings/upcoming?limit=${limit}`);
    return response.data;
};

export const updateMeetingAttendance = async (meetingId: string, attendance: boolean): Promise<boolean> => {
    const response = await axiosCore.patch(`/api/meetings/${meetingId}/attendance`, {
        attendance,
    });

    return response.data.attendance;
};

/**
 * Récupère les destinataires possibles pour la délégation de pouvoir de l'élu connecté
 * (membres présents ou n'ayant pas répondu, et disponibilité de son suppléant).
 */
export const getMeetingProxyEligibility = async (meetingId: string): Promise<ProxyEligibilityDto> => {
    const response = await axiosCore.get(`/api/meetings/${meetingId}/proxy-eligibility`);
    return response.data;
};

export interface GiveMeetingProxyResultDto {
    myProxyGivenTo: MyProxyGivenToDto | null;
    isNewParticipant: boolean;
    convocationGenerated: boolean;
}

/**
 * Donne le pouvoir de l'élu connecté à un autre membre de la réunion ou à son suppléant.
 */
export const giveMeetingProxy = async (
    meetingId: string,
    target: 'member' | 'suppleant',
    recipientReunionEluId?: string
): Promise<GiveMeetingProxyResultDto> => {
    const response = await axiosCore.post(`/api/meetings/${meetingId}/proxy`, {
        target,
        recipientReunionEluId,
    });

    return response.data;
};

/**
 * Annule le pouvoir donné par l'élu connecté pour cette réunion.
 */
export const revokeMeetingProxy = async (meetingId: string): Promise<{ myProxyGivenTo: null }> => {
    const response = await axiosCore.delete(`/api/meetings/${meetingId}/proxy`);
    return response.data;
};

/**
 * Récupération des documents.
 * @param idMeeting identifiant due la réunion
 * @param mandateId identifiant du mandat
 */
export const getMeetingFiles = async (idMeeting: string, mandateId: string): Promise<MeetingFileMetadataDto[]> => {
    // Selon le parametre de l'état, on adapte la requete
    const response = await axiosCore.get(`/api/meetings/${idMeeting}/mandate/${mandateId}/documents`);
    return response.data;
};

export const getMeetingFilesForCurrentUser = async (idMeeting: string): Promise<MeetingFileMetadataDto[]> => {
    const response = await axiosCore.get(`/api/meetings/${idMeeting}/documents/current-user`);
    return response.data;
};

/**
 * Récupération du contenu binaire d'un document de réunion.
 * @param idMeeting identifiant de la réunion
 * @param documentId identifiant du document
 */
export const getMeetingFileContent = async (
    idMeeting: string,
    mandateId: string,
    documentId: string
): Promise<Blob> => {
    const response = await axiosCore.get(
        `/api/meetings/${idMeeting}/mandate/${mandateId}/documents/${documentId}/content`,
        {
            responseType: 'blob',
        }
    );

    return response.data;
};

export const getMeetingFileContentForCurrentUser = async (idMeeting: string, documentId: string): Promise<Blob> => {
    const response = await axiosCore.get(`/api/meetings/${idMeeting}/documents/current-user/${documentId}/content`, {
        responseType: 'blob',
    });

    return response.data;
};
